import { computed, reactive, watchEffect } from "vue";

import envOptions from "@/helpers/Env";
import { i18n } from "@/i18n";
import {
  ModelNotFoundError,
  ModeldataNotFoundError,
  NotAuthenticatedError,
  NotFoundError
} from "@core/errors/Errors";

import eventHub from "@/helpers/utility/Events";
import * as API from "@/api/API";
import { dateOrNow } from "@/helpers/utility/DateTimeFunctions";
import { showConflictModelPopup } from "@manager/workspacemodels/ModelConflictScreen.js";

import { useUser } from "@/api/user.store";
import { useMediaStore } from "@core/media/media.store";
import { modelDataAPI } from "./modelVersionAPI";
import { modelAPI, modelCloudAPI, modelLocalAPI } from "./modelAPI";
import type { Model } from "./Model";
import type { LocalModelInfo } from "@/api/DB";
import type {
  ModelVersion,
  ModelVersionMeta,
  ModelVersionWithData
} from "./ModelVersion";
import type { ModelPublication } from "./ModelPublication";
import type { ModelShare } from "./ModelShare";
import type { User } from "./User";

const models = reactive<{
  [modelId: string]: Model | LocalModelInfo;
}>({});
const loadingmodels = reactive<{ [modelId: string]: boolean }>({});

/**
 * Updates a local registry entry.
 * Does not save the entry to the server!
 */
const updateModelEntry = (
  modelId: number | string,
  updatedModel: LocalModelInfo | null | undefined
) => {
  if (updatedModel == null || updatedModel.deleted) {
    delete models[modelId];
    return;
  }

  // prefer saving under the id specified in the model
  const modelIdToSave = updatedModel.id ?? modelId;

  const entry = models[modelId];
  if (entry) {
    const oldId = entry.id;
    const updatedEntry = Object.assign(entry, updatedModel);

    if (oldId != modelIdToSave) {
      models[modelIdToSave] = updatedEntry;
      delete models[oldId];
      eventHub.emit("model-id-changed", oldId, modelIdToSave);
    }
  } else {
    models[modelIdToSave] = updatedModel;
  }
};

eventHub.on(
  "db-model-updated",
  (modelId: number | string, updatedModel: Model) => {
    updateModelEntry(modelId, updatedModel);
  }
);

export function useModels(props?: { modelId?: number | string }) {
  const { user, isSyncEnabled } = useUser();

  const currentModel = computed(() => {
    if (!props) return null;
    if (props.modelId == null) return null;
    return models?.[props?.modelId];
  });

  const isLoading = computed(() => {
    if (!props) return false;
    if (props.modelId == null) return false;
    return loadingmodels?.[props?.modelId] == true;
  });

  /**
   * Return the models in that folder.
   * Does not make a network request, only filters the cached models.
   * @returns All locally cached models in that folder
   */
  function getModelsFilteredByFolder(folderId: number) {
    return Object.values(models).filter((m) => m.folder === folderId);
  }

  const byWorkspace = computed(() => {
    return Object.values(models).reduce((acc, model) => {
      if (model == null) return acc;
      if (acc[model.workspace] == null) {
        acc[model.workspace] = [];
      }
      acc[model.workspace].push(model);
      return acc;
    }, {} as { [workspaceId: number]: Model[] });
  });

  // ====================
  // ==== MODEL INFO ====

  /**
   * Load a model and put it in memory store.
   */
  async function getModelInfo(
    modelId: number | string,
    requestOptions: API.RequestOptions<Model> = {}
  ) {
    loadingmodels[modelId] = true;

    try {
      const modelInfo = await modelAPI.get(modelId, requestOptions);

      updateModelEntry(modelInfo.id, modelInfo);

      return modelInfo;
    } catch (error) {
      if (error instanceof NotFoundError) {
        // remove the local cache entry
        updateModelEntry(modelId, null);

        throw new ModelNotFoundError(error.message);
      }
      throw error;
    } finally {
      loadingmodels[modelId] = false;
    }
  }

  /**
   * Save a model to the database.
   */
  async function updateModel(
    modelInfo: { id: number | string; workspace: number } & Partial<Model>,
    { synchAfterwards = true } = {}
  ) {
    const modelId = modelInfo.id;
    const modelEntry = models[modelId];

    if (modelEntry == null) {
      console.warn(`Model ${modelId} not found.`);
      throw new NotFoundError();
    }

    const workspaceId = modelEntry.workspace;

    const updatedModel = await modelAPI.update(workspaceId, modelId, {
      data: modelInfo
    });

    // sync:
    if (synchAfterwards && useUser().isSyncEnabled()) {
      modelCloudAPI
        .update(workspaceId, modelId, { data: modelInfo })
        .then((updatedRemoteModel) => {
          updateModelEntry(updatedRemoteModel.id, updatedRemoteModel);
          return modelLocalAPI.update(workspaceId, modelId, {
            data: {
              ...updatedRemoteModel,
              lastSynch: dateOrNow(updatedRemoteModel.modified)
            }
          });
        })
        .then((updatedLocalModel) => {
          console.log("> synched local model upwards", updatedLocalModel);
        })
        .catch((error) => {
          console.warn(error.status, error.message);
        });
    }

    if (!updatedModel) {
      throw new NotFoundError();
    }

    updateModelEntry(updatedModel.id, updatedModel);

    return updatedModel;
  }

  /**
   * Create a new model entry.
   */
  async function createModel(
    modelInfo: {
      name: string;
      folder: number;
      workspace: number;
    },
    requestOptions: API.RequestOptions<Model> = {}
  ) {
    const newModel = await modelAPI.create(modelInfo, requestOptions);

    if (!newModel) {
      throw new Error("Creating new model failed.");
    }

    console.debug("%c/create", "color: green;", newModel);
    if (newModel) {
      updateModelEntry(newModel.id, newModel);
    }
    return newModel;
  }

  async function destroyModel(modelInfo: {
    id: number | string;
    workspace: number;
  }) {
    const workspaceId = modelInfo?.workspace;
    const modelId = modelInfo?.id;

    await modelAPI.destroy(workspaceId, modelId);

    updateModelEntry(modelId, null);

    return true;
  }

  async function copyModel(
    modelInfo: Model,
    options: {
      workspaceId: number;
      folderId: number;
      name: string;
      description?: string;
    },
    requestOptions: API.RequestOptions<Model>
  ) {
    const { folderId, workspaceId, name, description } = options;
    const modelId = modelInfo.id;
    const sourceWorkspaceId = modelInfo.workspace;

    const data: {
      folderId: number;
      workspaceId: number;
      name: string;
      description?: string;
    } = {
      folderId,
      workspaceId,
      name: name
    };
    if (description) {
      data.description = description;
    }

    const newModel = await modelAPI.copy(sourceWorkspaceId, modelId, {
      ...requestOptions,
      data
    });

    if (newModel == null) {
      throw new Error("Copy failed.");
    }

    updateModelEntry(newModel.id, newModel);

    return newModel;
  }

  async function moveModel(data: {
    id: Model["id"];
    workspaceId: Model["workspace"];
    folderId: Model["folder"];
    folderSlug: Model["folderSlug"];
  }) {
    const updatedModel = await updateModel({
      id: data.id,
      workspace: data.workspaceId,
      folder: data.folderId,
      folderSlug: data.folderSlug
    });

    updateModelEntry(updatedModel.id, updatedModel);

    return updatedModel;
  }

  // FOLDERS
  /**
   * Load all models of a folder
   */
  async function getFolderModels(
    workspace: { id: number },
    folderSlug: string,
    { waitForSync = false, synchErrorsMuted = true } = {},
    requestOptions: API.RequestOptions<Model[]> = {}
  ): Promise<Model[]> {
    const workspaceId = workspace?.id;
    if (workspaceId == null) return [];

    requestOptions.query = {
      ...(requestOptions?.query ?? {}),
      workspace: workspaceId,
      folder_slug: folderSlug
    };

    // get all cached models that are currently in this folder:
    const cachedModelIds = Object.values(models)
      .filter((m) => m.workspace === workspaceId && m.folderSlug === folderSlug)
      .map((m) => m.id);

    try {
      const localModels = await modelAPI.list(requestOptions);

      for (const model of localModels) {
        updateModelEntry(model.id, model);

        // remove from cached model ids
        const i = cachedModelIds.indexOf(model.id);
        if (i > -1) {
          cachedModelIds.splice(i, 1);
        }
      }

      console.log("old model ids", cachedModelIds);

      // remove all models that are not in this folder anymore:
      for (const oldModelId of cachedModelIds) {
        updateModelEntry(oldModelId, null);
      }

      if (isSyncEnabled()) {
        const syncRequest = modelLocalAPI
          // pass in the requestoptions to ONLY synch the folder models:
          .syncAll(requestOptions)
          .catch((_error) => {
            if (!synchErrorsMuted) throw _error;
            console.warn("hidden error", _error);
          });

        if (waitForSync) await syncRequest;
      }

      return localModels;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  // SYNC FUNCTIONS

  /**
   * 
   * @param options
   * @param options.waitForSync Wait until all models are synched before returning
   * @param options.synchErrorsMuted Do not throw errors that occur during synching
   * @returns 
   */
  async function loadAllModels({
    waitForSync = false,
    synchErrorsMuted = true
  } = {}) {
    const cachedModelIds = Object.values(models).map((i) => i.id);

    const localModels = await modelAPI.list();

    for (const model of localModels) {
      updateModelEntry(model.id, model);

      const i = cachedModelIds.indexOf(model.id);
      if (i > -1) {
        cachedModelIds.splice(i, 1);
      }
    }

    // Remove all cached models that do not exist anymore:
    for (const cachedModelId of cachedModelIds) {
      updateModelEntry(cachedModelId, null);
    }

    if (isSyncEnabled()) {
      const syncRequest = modelLocalAPI.syncAll().catch((_error) => {
        if (!synchErrorsMuted) throw _error;
        console.warn("hidden error", _error);
      });

      if (waitForSync) {
        const synchedModels = await syncRequest;
        if (synchedModels) {
          return synchedModels;
        }
      }
    }

    return localModels;
  }

  async function synchModel(modelId: number | string) {
    const model = await modelLocalAPI.loadAndSynch(modelId);

    updateModelEntry(modelId, model);

    return model;
  }

  // ====================
  // ==== MODEL DATA ====

  /**
   * Get and decompress the data of an online model file
   * @returns the encrypted model data
   */
  async function getModelData(
    workspaceId: number,
    modelId: number | string,
    requestOptions: API.RequestOptions = {},
    omitMedia: boolean = false
  ) {
    try {
      const modelVersion = await modelDataAPI.get(
        workspaceId,
        modelId,
        requestOptions,
        omitMedia
      );
      if (modelVersion == null) {
        throw new ModeldataNotFoundError();
      }
      return modelVersion;
    } catch (error) {
      if (error instanceof NotFoundError) {
        console.warn(`Modeldata for model ${modelId} does not exist yet.`);
        return {
          id: null,
          data: null,
          model: toIntIfNotUuid(modelId),
          created: new Date().toISOString(),
          media: []
        } as ModelVersionWithData;
      } else {
        throw error;
      }
    }
  }

  /**
   * Compress data and send it to the online plattform, to be saved.
   */
  async function updateModelData(
    modelInfo: {
      id: string | number;
      workspace: number;
      name: string;
      lastVersion?: Partial<ModelVersion>;
      folder: number;
    },
    newModelVersion: {
      data: string;
      newMediaUUIDs?: string[];
      media: { uuid: string; data: Blob }[];
      appVersion?: string | null;
    },
    { isPlayerProject = false } = {}
  ): Promise<ModelVersion> {
    const encryptedData = newModelVersion.data;
    if (encryptedData == null) {
      throw new Error("No data to save was passed.");
    }

    const modelId = toIntIfNotUuid(modelInfo.id);
    const workspaceId = modelInfo.workspace;

    // the data is saved under the current modelId
    if (modelId == null) {
      throw new Error("Model id required");
    }
    if (workspaceId == null) {
      throw new Error("workspace id required");
    }

    const newMediaUUIDs = newModelVersion?.newMediaUUIDs ?? [];

    // set the filename for the download file
    const fileName = `${modelInfo.name}.${isPlayerProject ? "sim" : "model"}`;

    try {
      const updatedData = await modelDataAPI.update(workspaceId, modelId, {
        modelData: encryptedData,
        fileName: fileName,
        lastVersionId: modelInfo?.lastVersion?.id ?? null,
        media: newModelVersion?.media ?? [],
        newMediaUUIDs: newMediaUUIDs,
        appVersion: newModelVersion?.appVersion ?? null
      });

      // remove the "new" markers in the media store
      // so that the images are not uploaded again
      // when saving again.
      useMediaStore().removeNewFlags(newMediaUUIDs);

      return updatedData;
    } catch (_error) {
      const error = _error as {
        status?: number;
        conflictingEntry?: ModelVersion;
      };
      // Handle save conflict:
      if (error.status === 409) {
        const conflictingEntry = error.conflictingEntry;
        const decision = await showConflictModelPopup(
          {
            ...modelInfo,
            lastVersion: {
              ...modelInfo.lastVersion,
              created: new Date().toISOString()
            }
          },
          { lastVersion: conflictingEntry },
          "data"
        );

        switch (decision) {
          case "both": {
            const _user = user.value;

            if (!_user) {
              throw new NotAuthenticatedError();
            }

            // * CREATE A NEW MODEL FOR THE CURRENT OPEN STATE
            // * UPLOAD THAT NEW MODEL
            // * RETURN THAT NEW MODELDATA
            // doesn't touch the remote model state
            const newModel = await createModel({
              ...modelInfo,
              name:
                modelInfo.name +
                i18n.t("labels.copySuffix") +
                ` ${_user.name}`
            });

            if (newModel == null) {
              throw new Error("Error while creating new model.");
            }

            return updateModelData(newModel, newModelVersion, {
              isPlayerProject
            });
          }

          case "local": {
            // OVERWRITE THE ONLINE SAVE:
            modelInfo.lastVersion = conflictingEntry;
            return await updateModelData(modelInfo, newModelVersion, {
              isPlayerProject
            });
          }

          case "remote": {
            // DISCARDS ANY OPEN LOCAL STATE
            // LOADS THE REMOTE MODEL STATE
            const modelData = await getModelData(
              modelInfo.workspace,
              modelInfo.id
            );
            return modelData;
          }
        }
      } else {
        throw error;
      }
    }

    throw new Error("Updating Modeldata failed.");
  }

  // ================
  // ==== SHARES ====

  async function shareModel(
    modelId: number | string,
    emails: { email: string }[],
    options = {}
  ) {
    const model = models[modelId];
    const newShares = await API.post<ModelShare[]>(
      `/v1/workspaces/${model.workspace}/models/${model.id}/shares/from_emails`,
      {
        ...options,
        data: emails
      }
    );

    // update or add new shares
    const shares = model.shares ?? [];
    for (const newShare of newShares) {
      const oldShare = shares.find((s) => s.id == newShare.id);
      if (oldShare) {
        Object.assign(oldShare, newShare);
      } else {
        shares.push(newShare);
      }
    }

    model.shares = shares;

    // update the local entry
    if (envOptions.saveLocally) {
      await modelLocalAPI.update(model.workspace, model.id, {
        data: model
      });
    }

    return newShares;
  }

  async function destroyShare(modelId: number, shareId: number) {
    const model = models[modelId];
    await API.destroy(
      `/v1/workspaces/${model.workspace}/models/${model.id}/shares/${shareId}`
    );
    model.shares = model.shares.filter((s) => s.id != shareId);

    // update the local entry
    if (envOptions.saveLocally) {
      await modelLocalAPI.update(model.workspace, model.id, {
        data: model
      });
    }

    return true;
  }

  async function updateShare(modelId: number, share: Partial<ModelShare>) {
    const model = models[modelId];
    const shareId = share.id;
    const updatedShare = await API.patch<ModelShare>(
      `/v1/workspaces/${model.workspace}/models/${model.id}/shares/${shareId}`,
      {
        data: share
      }
    );
    const originalShare = (model.shares || []).find((s) => s.id == shareId);
    if (originalShare) {
      Object.assign(originalShare, updatedShare);

      // update the local entry
      if (envOptions.saveLocally) {
        await modelLocalAPI.update(model.workspace, model.id, {
          data: model
        });
      }
    }

    return updatedShare;
  }

  // ====================
  // === PUBLICATIONS ===

  /**
   * Get all publications of a model.
   */
  async function loadModelPublications(modelId: number | string) {
    const model = models[modelId];
    if (model == null) {
      console.warn(`no model ${modelId} found`);
      return [];
    }
    const workspaceId = model.workspace;
    const publications = await API.get<ModelPublication[]>(
      `/v1/workspaces/${workspaceId}/models/${modelId}/publications`
    );
    model.publications = publications;

    // update the local entry
    if (envOptions.saveLocally) {
      await modelLocalAPI.update(model.workspace, model.id, {
        data: model
      });
    }

    return publications;
  }

  async function createModelPublication(
    modelId: number,
    publicationData: Partial<ModelPublication>,
    requestOptions: API.RequestOptions<ModelPublication> = {}
  ) {
    const model = models[modelId];
    const workspaceId = model.workspace;
    const publication = await API.post<ModelPublication>(
      `/v1/workspaces/${workspaceId}/models/${modelId}/publications`,
      {
        ...requestOptions,
        data: publicationData
      }
    );
    const modelPublications = model?.publications ?? [];
    modelPublications.push(publication);
    model.publications = modelPublications;

    // update the local entry
    if (envOptions.saveLocally) {
      await modelLocalAPI.update(model.workspace, model.id, {
        data: model
      });
    }

    return publication;
  }

  async function updateModelPublication(
    modelId: number | string,
    publicationData: Partial<ModelPublication> & { id: string },
    requestOptions: API.RequestOptions<ModelPublication> = {}
  ) {
    const model = models[modelId];
    const workspaceId = model.workspace;
    const publicationId = publicationData.id;
    const publication = await API.patch<ModelPublication>(
      `/v1/workspaces/${workspaceId}/models/${modelId}/publications/${publicationId}`,
      {
        ...requestOptions,
        data: publicationData
      }
    );
    const modelPublications = model?.publications ?? [];
    const oldPublicationIndex = modelPublications.findIndex(
      (p) => p.id == publicationId
    );
    modelPublications.splice(oldPublicationIndex, 1, publication);
    model.publications = modelPublications;

    // update the local entry
    if (envOptions.saveLocally) {
      await modelLocalAPI.update(model.workspace, model.id, {
        data: model
      });
    }

    return publication;
  }

  async function destroyModelPublication(
    modelId: number | string,
    publicationId: string
  ) {
    const model = models[modelId];
    await API.destroy(
      `/v1/workspaces/${model.workspace}/models/${model.id}/publications/${publicationId}`
    );
    model.publications = (model.publications ?? []).filter(
      (s) => s.id != publicationId
    );

    // update the local entry
    if (envOptions.saveLocally) {
      await modelLocalAPI.update(model.workspace, model.id, {
        data: model
      });
    }

    return true;
  }

  // ======================
  // === MODEL VERSIONS ===

  async function loadModelVersionHistory(modelInfo: Model) {
    const modelId = modelInfo?.id;
    const model = models[modelId];
    const workspaceId = model.workspace;
    const versions = await API.get<ModelVersionMeta[]>(
      `/v1/workspaces/${workspaceId}/models/${modelId}/versions`
    );

    model.versions = versions;

    // update the local entry
    if (envOptions.saveLocally) {
      await modelLocalAPI.update(model.workspace, model.id, {
        data: model
      });
    }

    return versions;
  }

  // ====================
  // === OTHER THINGS ===

  /**
   * Waits until a model is loaded and resolves it afterwards.
   */
  function waitForModel(
    modelId: number | string
  ): Promise<Model | LocalModelInfo> {
    return new Promise((resolve) => {
      if (models[modelId] != null) {
        resolve(models[modelId]);
      } else {
        const stop = watchEffect(() => {
          if (models[modelId] != null) {
            resolve(models[modelId]);
            stop();
          }
        });
      }
    });
  }

  return {
    models,
    currentModel,
    isLoading,
    byWorkspace,

    loadAllModels,
    synchModel,

    updateModelEntry,

    updateModel,
    destroyModel,
    createModel,
    getModelInfo,
    copyModel,
    moveModel,

    getModelData,
    updateModelData,

    getFolderModels,
    getModelsFilteredByFolder,

    shareModel,
    updateShare,
    destroyShare,

    createModelPublication,
    loadModelPublications,
    updateModelPublication,
    destroyModelPublication,

    loadModelVersionHistory,

    showConflictModelPopup,
    waitForModel
  };
}

/**
 * Makes sure not to transform uuids
 * @returns The number as int or the uuid
 */
function toIntIfNotUuid(str: string | number) {
  if (typeof str != "string") return str;
  if (str.includes("-")) {
    return str;
  }
  return parseInt(str);
}

function reset() {
  for (const modelId in models) {
    delete models[modelId];
  }
}
eventHub.on("user-changed", async (user: User | null) => {
  console.log("%creseting workspace models", "color: lightgrey;");
  reset();
});

// /**
//  * @typedef {Object} ModelInfo
//  * @property {Number} id
//  * @property {String} name
//  * @property {String} description
//  * @property {Number} workspace ID of the workspace
//  * @property {Number} folder ID of the folder
//  * @property {String} folderSlug
//  * @property {String} created
//  * @property {String} modified
//  * @property {Number} deleted timestamp of deletion, only on local models
//  * @property {ModelDataEntry} lastVersion latest known version, can be local or remote
//  * @property {Array?} publications
//  */

// /**
//  * @typedef {Object} ModelDataEntry
//  * @property {string} data
//  * @property {number} id
//  * @property {number?} model
//  * @property {date?} created
//  * @property {number?} lastSynch timestamp, only on local models
//  */

// /**
//  * @typedef {Object} Publication
//  * @property {string} id
//  * @property {boolean} hasPassword
//  * @property {boolean} isExampleModel
//  * @property {boolean} canFork
//  * @property {string} endsAt datetime ISO string
//  * @property {string} startsAt datetime ISO string
//  * @property {string} description
//  */
